import React, { useState } from 'react';
import { useToast } from './Toast';
import './CheckDetailModal.css';
import { CHECK_METADATA } from '../data/check-metadata';

interface CheckDetailModalProps {
  checkId: string;
  checkResult?: {
    status: 'pass' | 'fail' | 'warn' | 'info' | 'unknown';
    value: string | number;
    message?: string;
  };
  onClose: () => void;
}

export const CheckDetailModal: React.FC<CheckDetailModalProps> = ({ 
  checkId, 
  checkResult,
  onClose 
}) => {
  const { toast } = useToast();
  const metadata = CHECK_METADATA[checkId];
  const [isEditingThreshold, setIsEditingThreshold] = useState(false);
  const [customThreshold, setCustomThreshold] = useState('');

  if (!metadata) {
    return null;
  }

  // Get current threshold from localStorage or metadata
  const getCurrentThreshold = () => {
    const saved = localStorage.getItem(`threshold_${checkId}`);
    if (saved) return saved;
    
    // Try to extract from checkResult value if it's a numeric threshold
    if (checkResult && typeof checkResult.value === 'string') {
      const match = checkResult.value.match(/(\d+(?:\.\d+)?)\s*(KB|MB|px|ms|s)?/);
      if (match) {
        return match[0]; // Return with unit
      }
    }
    
    // Extract from metadata if available
    if (metadata.profileSpecific?.google?.threshold) {
      return metadata.profileSpecific.google.threshold;
    }
    return null;
  };

  const currentThreshold = getCurrentThreshold();
  const hasThreshold = !!currentThreshold;

  const saveThreshold = () => {
    if (customThreshold) {
      localStorage.setItem(`threshold_${checkId}`, customThreshold);
      setIsEditingThreshold(false);
      // TODO: Trigger re-validation with new threshold
      toast(`Threshold saved: ${customThreshold}. Reload and re-validate to apply.`, 'success');
    }
  };

  const resetThreshold = () => {
    localStorage.removeItem(`threshold_${checkId}`);
    setCustomThreshold('');
    setIsEditingThreshold(false);
  };

  const renderAcceptedValues = () => {
    if (!metadata.acceptedValues) return null;

    if (Array.isArray(metadata.acceptedValues)) {
      return (
        <div className="accepted-values-grid">
          {metadata.acceptedValues.map((value, idx) => (
            <div key={idx} className="value-chip">{value}</div>
          ))}
        </div>
      );
    }

    return (
      <div className="accepted-values-categories">
        {Object.entries(metadata.acceptedValues).map(([category, values]) => (
          <div key={category} className="value-category">
            <h4>{category}</h4>
            <div className="accepted-values-grid">
              {values.map((value, idx) => (
                <div key={idx} className="value-chip">{value}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="check-detail-modal">
        <div className="modal-header">
          <h2>{metadata.title}</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          {/* Current Result */}
          {checkResult && (
            <div className={`current-result ${checkResult.status}`}>
              <div className="result-label">Current Result</div>
              <div className="result-value">
                <span className={`status-badge ${checkResult.status}`}>
                  {checkResult.status === 'pass' && '✓'}
                  {checkResult.status === 'fail' && '✗'}
                  {checkResult.status === 'warn' && '⚠'}
                  {checkResult.status === 'info' && '○'}
                </span>
                <span className="value">{checkResult.value}</span>
              </div>
              {checkResult.message && (
                <div className="result-message">{checkResult.message}</div>
              )}
            </div>
          )}

          {/* Validation Policy (Threshold) - Editable */}
          {hasThreshold && (
            <div className="section threshold-section">
              <h3>Validation Policy</h3>
              {!isEditingThreshold ? (
                <div className="threshold-display">
                  <div className="threshold-value">
                    Current Threshold: <strong>{currentThreshold}</strong>
                  </div>
                  <div className="threshold-actions">
                    <button 
                      className="edit-threshold-btn"
                      onClick={() => {
                        setCustomThreshold(currentThreshold || '');
                        setIsEditingThreshold(true);
                      }}
                    >
                      ✎ Customize
                    </button>
                  </div>
                </div>
              ) : (
                <div className="threshold-editor">
                  <input 
                    type="text"
                    value={customThreshold}
                    onChange={(e) => setCustomThreshold(e.target.value)}
                    placeholder="e.g., 100 KB, 150px, 30s"
                    className="threshold-input"
                    autoFocus
                  />
                  <div className="threshold-buttons">
                    <button onClick={saveThreshold} className="save-btn">Save</button>
                    <button onClick={resetThreshold} className="reset-btn">Reset</button>
                    <button onClick={() => setIsEditingThreshold(false)} className="cancel-btn">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div className="section">
            <p className="description">{metadata.description}</p>
          </div>

          {/* Why This Test */}
          <div className="section">
            <h3>Why this test?</h3>
            <p>{metadata.whyThisTest}</p>
          </div>

          {/* Accepted Values */}
          {metadata.acceptedValues && (
            <div className="section">
              <h3>Accepted Values</h3>
              {renderAcceptedValues()}
            </div>
          )}

          {/* Profile-Specific Rules */}
          {metadata.profileSpecific && (
            <div className="section">
              <h3>Profile-Specific Requirements</h3>
              <div className="profile-rules">
                {metadata.profileSpecific.iab && (
                  <div className="profile-rule">
                    <span className="profile-name">IAB Baseline:</span>
                    <span className={`severity ${metadata.profileSpecific.iab.severity}`}>
                      {metadata.profileSpecific.iab.severity.toUpperCase()}
                    </span>
                    {metadata.profileSpecific.iab.threshold && (
                      <span className="threshold">
                        Threshold: {metadata.profileSpecific.iab.threshold}
                      </span>
                    )}
                  </div>
                )}
                {metadata.profileSpecific.google && (
                  <div className="profile-rule">
                    <span className="profile-name">Google Ads:</span>
                    <span className={`severity ${metadata.profileSpecific.google.severity}`}>
                      {metadata.profileSpecific.google.severity.toUpperCase()}
                    </span>
                    {metadata.profileSpecific.google.threshold && (
                      <span className="threshold">
                        Threshold: {metadata.profileSpecific.google.threshold}
                      </span>
                    )}
                  </div>
                )}
                {metadata.profileSpecific.facebook && (
                  <div className="profile-rule">
                    <span className="profile-name">Meta/Facebook:</span>
                    <span className={`severity ${metadata.profileSpecific.facebook.severity}`}>
                      {metadata.profileSpecific.facebook.severity.toUpperCase()}
                    </span>
                    {metadata.profileSpecific.facebook.threshold && (
                      <span className="threshold">
                        Threshold: {metadata.profileSpecific.facebook.threshold}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Remediation */}
          {metadata.remediation && (
            <div className="section remediation">
              <h3>How to Fix</h3>
              <p>{metadata.remediation}</p>
            </div>
          )}

          {/* Learn More */}
          {metadata.learnMoreUrl && (
            <div className="section">
              <a 
                href={metadata.learnMoreUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="learn-more-link"
              >
                Learn More →
              </a>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="primary-btn">Close</button>
        </div>
      </div>
    </>
  );
};